import { collection, deleteDoc, doc, getDocs, query, where, writeBatch } from 'firebase/firestore';
import { db } from '../../config';
import { handleFirebaseError } from '../../error-handling';

/**
 * Deletes a group and all its related data without permission checks
 * Used internally for organization cleanup
 */
async function deleteGroupData(groupId: string, batch: any) {
  console.log(`🗑️ Deleting group data for group: ${groupId}`);
  
  // Delete group members
  const membersSnapshot = await getDocs(
    query(collection(db, 'groupMembers'), where('groupId', '==', groupId))
  );
  membersSnapshot.docs.forEach(doc => batch.delete(doc.ref));
  console.log(`📋 Found ${membersSnapshot.docs.length} group members to delete`);

  // Delete group messages
  const messagesSnapshot = await getDocs(
    query(collection(db, 'groupMessages'), where('groupId', '==', groupId))
  );
  messagesSnapshot.docs.forEach(doc => batch.delete(doc.ref));
  console.log(`📋 Found ${messagesSnapshot.docs.length} group messages to delete`);

  // Delete group posts
  const postsSnapshot = await getDocs(
    query(collection(db, 'groupPosts'), where('groupId', '==', groupId))
  );
  postsSnapshot.docs.forEach(doc => batch.delete(doc.ref));
  console.log(`📋 Found ${postsSnapshot.docs.length} group posts to delete`);

  // Delete group hierarchical data (if any)
  const hierarchicalSnapshot = await getDocs(
    query(collection(db, 'hierarchicalMembers'), where('groupId', '==', groupId))
  );
  hierarchicalSnapshot.docs.forEach(doc => batch.delete(doc.ref));
  console.log(`📋 Found ${hierarchicalSnapshot.docs.length} hierarchical memberships to delete`);

  // Delete group virtual data (if any)
  const virtualSnapshot = await getDocs(
    query(collection(db, 'virtualMembers'), where('groupId', '==', groupId))
  );
  virtualSnapshot.docs.forEach(doc => batch.delete(doc.ref));
  console.log(`📋 Found ${virtualSnapshot.docs.length} virtual memberships to delete`);

  // Delete group competition data (if any)
  const competitionSnapshot = await getDocs(
    query(collection(db, 'groupCompetition'), where('groupId', '==', groupId))
  );
  competitionSnapshot.docs.forEach(doc => batch.delete(doc.ref));
  console.log(`📋 Found ${competitionSnapshot.docs.length} group competition data to delete`);

  // Delete group settings (if any)
  const settingsSnapshot = await getDocs(
    query(collection(db, 'groupSettings'), where('groupId', '==', groupId))
  );
  settingsSnapshot.docs.forEach(doc => batch.delete(doc.ref));
  console.log(`📋 Found ${settingsSnapshot.docs.length} group settings to delete`);

  // Delete the group document itself
  batch.delete(doc(db, 'supportGroups', groupId));
  console.log(`✅ Group ${groupId} and all related data marked for deletion`);
}

export async function deleteOrganization(organizationId: string): Promise<boolean> {
  try {
    console.log(`🗑️ Starting comprehensive organization deletion for: ${organizationId}`);
    
    const batch = writeBatch(db);
    let totalDeletions = 0;

    // 1. Delete organization document
    const orgRef = doc(db, 'organizations', organizationId);
    batch.delete(orgRef);
    totalDeletions++;
    console.log(`📋 Organization document marked for deletion`);

    // 2. Delete all organization members
    const membersSnapshot = await getDocs(
      query(collection(db, 'organizationMembers'), 
      where('organizationId', '==', organizationId))
    );
    membersSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    console.log(`📋 Found ${membersSnapshot.docs.length} organization members to delete`);
    totalDeletions += membersSnapshot.docs.length;

    // 3. Delete organization default habits
    const defaultHabitsSnapshot = await getDocs(
      query(collection(db, 'organizationDefaultHabits'), 
      where('organizationId', '==', organizationId))
    );
    defaultHabitsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    console.log(`📋 Found ${defaultHabitsSnapshot.docs.length} organization default habits to delete`);
    totalDeletions += defaultHabitsSnapshot.docs.length;

    // 4. Delete organization settings (if any)
    const settingsSnapshot = await getDocs(
      query(collection(db, 'organizationSettings'), 
      where('organizationId', '==', organizationId))
    );
    settingsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    console.log(`📋 Found ${settingsSnapshot.docs.length} organization settings to delete`);
    totalDeletions += settingsSnapshot.docs.length;

    // 5. Delete organization permissions (if any)
    const permissionsSnapshot = await getDocs(
      query(collection(db, 'organizationPermissions'), 
      where('organizationId', '==', organizationId))
    );
    permissionsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    console.log(`📋 Found ${permissionsSnapshot.docs.length} organization permissions to delete`);
    totalDeletions += permissionsSnapshot.docs.length;

    // 6. Delete organization managers
    const managersSnapshot = await getDocs(
      query(collection(db, 'organizationManagers'), 
      where('organizationId', '==', organizationId))
    );
    managersSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    console.log(`📋 Found ${managersSnapshot.docs.length} organization managers to delete`);
    totalDeletions += managersSnapshot.docs.length;

    // 7. Delete organization examiners
    const examinersSnapshot = await getDocs(
      query(collection(db, 'organizationExaminers'), 
      where('organizationId', '==', organizationId))
    );
    examinersSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    console.log(`📋 Found ${examinersSnapshot.docs.length} organization examiners to delete`);
    totalDeletions += examinersSnapshot.docs.length;

    // 8. Delete organization instructors
    const instructorsSnapshot = await getDocs(
      query(collection(db, 'organizationInstructors'), 
      where('organizationId', '==', organizationId))
    );
    instructorsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    console.log(`📋 Found ${instructorsSnapshot.docs.length} organization instructors to delete`);
    totalDeletions += instructorsSnapshot.docs.length;

    // 9. Delete organization statistics (if any)
    const statisticsSnapshot = await getDocs(
      query(collection(db, 'organizationStatistics'), 
      where('organizationId', '==', organizationId))
    );
    statisticsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    console.log(`📋 Found ${statisticsSnapshot.docs.length} organization statistics to delete`);
    totalDeletions += statisticsSnapshot.docs.length;

    // 10. Delete organization competition data (if any)
    const competitionSnapshot = await getDocs(
      query(collection(db, 'organizationCompetition'), 
      where('organizationId', '==', organizationId))
    );
    competitionSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    console.log(`📋 Found ${competitionSnapshot.docs.length} organization competition data to delete`);
    totalDeletions += competitionSnapshot.docs.length;

    // 11. Delete organization hierarchical data (if any)
    const hierarchicalSnapshot = await getDocs(
      query(collection(db, 'hierarchicalMembers'), 
      where('organizationId', '==', organizationId))
    );
    hierarchicalSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    console.log(`📋 Found ${hierarchicalSnapshot.docs.length} hierarchical memberships to delete`);
    totalDeletions += hierarchicalSnapshot.docs.length;

    // 12. Delete organization virtual data (if any)
    const virtualSnapshot = await getDocs(
      query(collection(db, 'virtualMembers'), 
      where('organizationId', '==', organizationId))
    );
    virtualSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    console.log(`📋 Found ${virtualSnapshot.docs.length} virtual memberships to delete`);
    totalDeletions += virtualSnapshot.docs.length;

    // 13. Delete organization examination data (if any)
    const examinationSnapshot = await getDocs(
      query(collection(db, 'examinations'), 
      where('organizationId', '==', organizationId))
    );
    examinationSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    console.log(`📋 Found ${examinationSnapshot.docs.length} organization examinations to delete`);
    totalDeletions += examinationSnapshot.docs.length;

    // 14. Delete organization habit data (if any)
    const habitSnapshot = await getDocs(
      query(collection(db, 'habits'), 
      where('organizationId', '==', organizationId))
    );
    habitSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    console.log(`📋 Found ${habitSnapshot.docs.length} organization habits to delete`);
    totalDeletions += habitSnapshot.docs.length;

    // 15. Delete all groups belonging to this organization
    const groupsSnapshot = await getDocs(
      query(collection(db, 'supportGroups'), 
      where('organization', '==', organizationId))
    );
    console.log(`📋 Found ${groupsSnapshot.docs.length} groups to delete`);
    
    // Delete each group and its related data
    for (const groupDoc of groupsSnapshot.docs) {
      await deleteGroupData(groupDoc.id, batch);
    }

    // Note: Customer examination results and personal data are preserved
    // when an organization is deleted. Customers should retain their exam history,
    // personal habits, and other individual data. Only organization-specific data
    // and memberships are deleted.

    // Commit all deletions
    await batch.commit();
    console.log(`✅ Successfully deleted organization ${organizationId} and all related data (${totalDeletions} total deletions)`);
    return true;
  } catch (error) {
    console.error(`❌ Error deleting organization ${organizationId}:`, error);
    handleFirebaseError(error, 'Error deleting organization and related data');
    return false;
  }
}